import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Stack,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { Appearance, loadStripe } from "@stripe/stripe-js";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Footer from "../../components/Footer";
import Header from "../../components/Header";
import { useNotification } from "../../context/NotificationContext";
import { bookingService } from "../../services/api/bookingService";
import { paymentService } from "../../services/api/paymentService";


// Initialize Stripe
const stripePromise = (() => {
  const stripeKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;

  if (!stripeKey || stripeKey.trim() === "") {
    console.warn(
      "⚠️ Stripe public key not found. Please add REACT_APP_STRIPE_PUBLIC_KEY to your environment variables."
    );
    return null;
  }

  try {
    return loadStripe(stripeKey);
  } catch (error) {
    console.error("❌ Failed to initialize Stripe:", error);
    return null;
  }
})();

interface CardInfo {
  number: string;
  expiry: string;
  cvc: string;
  type: "VISA" | "MASTERCARD";
}

interface BookingDetails {
  guestInfo?: {
    name: string;
    email: string;
    phone: string;
    checkIn: string;
    checkOut: string;
    guests: number;
    roomType: string;
  };
  selectedInsurance?: string;
  hasOverstayProtection?: boolean;
  baseRate: number;
  insurancePrice: number;
  overstayPrice: number;
  taxesAndFees: number;
  total: number;
  botelId?: string;
  botelName?: string;
  checkIn?: string;
  duration?: string;
  bookingId: string;
  isExtension?: boolean;
}

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const PageContainer = styled(Box)({
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "rgba(0,0,0,0.7)",
  width: "100%",
  margin: 0,
  padding: 0,
  overflow: "hidden",
});

const HeroSection = styled(Box)({
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundImage: "url(images/background.jpg)",
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  minHeight: "100vh",
  padding: "80px 0",
  flexDirection: "column",
  margin: 0,
  left: 0,
  right: 0,
});

// Custom styled components for specific styling needs
const StyledCard = styled(Card)(({ theme }) => ({
  background: "#181D23",
  borderRadius: theme.breakpoints.down("md") ? 16 : 20,
  boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
  color: "#fff",
  marginBottom: 0,
  width: "100%",
  [theme.breakpoints.down("md")]: {
    borderRadius: 16,
    marginBottom: 16,
    padding: theme.breakpoints.down("sm") ? "16px" : "20px",
  },
}));

const StyledButton = styled(Button)<{ selected?: boolean }>(
  ({ selected, theme }) => ({
    background: selected ? "#2563EB" : "#232933",
    color: "#fff",
    borderRadius: 8,
    padding: theme.breakpoints.down("md") ? "6px 12px" : "8px 16px",
    fontWeight: 600,
    fontSize: theme.breakpoints.down("md") ? 12 : 14,
    border: `2px solid ${selected ? "#2563EB" : "#232933"}`,
    textTransform: "none",
    "&:hover": {
      background: selected ? "#1d4ed8" : "#2d3748",
      borderColor: selected ? "#1d4ed8" : "#2d3748",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: 11,
      padding: "4px 8px",
      minWidth: "auto",
    },
  })
);

const PaymentButton = styled(Button)(({ theme }) => ({
  width: "100%",
  background: "#2563EB",
  color: "#fff",
  borderRadius: 8,
  padding: theme.breakpoints.down("md") ? "10px 0" : "12px 0",
  fontWeight: 600,
  fontSize: theme.breakpoints.down("md") ? 14 : 16,
  textTransform: "none",
  "&:hover": {
    background: "#1d4ed8",
  },
  "&:disabled": {
    background: "#6b7280",
    opacity: 0.7,
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: 13,
    padding: "8px 0",
  },
}));

const MainContent = styled(Container)(({ theme }) => ({
  width: "100%",
  maxWidth: 1200,
  margin: "0 auto",
  marginTop: theme.breakpoints.down("md") ? 50 : 70,
  display: "flex",
  flexDirection: "column",
  gap: theme.breakpoints.down("md") ? 24 : 32,
  [theme.breakpoints.down("md")]: {
    marginTop: 50,
    gap: 24,
    padding: "0 16px",
  },
  [theme.breakpoints.down("sm")]: {
    marginTop: 30,
    gap: 16,
    padding: "0 12px",
  },
}));

const BookingSummaryCard = styled(Card)(({ theme }) => ({
  background: "#232933",
  borderRadius: theme.breakpoints.down("md") ? 16 : 20,
  padding: theme.breakpoints.down("md") ? "20px 16px" : "24px",
  marginBottom: theme.breakpoints.down("md") ? 16 : 24,
  [theme.breakpoints.down("sm")]: {
    padding: "16px 12px",
    marginBottom: 12,
  },
}));

const SummaryRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.breakpoints.down("md") ? 8 : 12,
  fontSize: theme.breakpoints.down("md") ? 14 : 16,
  [theme.breakpoints.down("sm")]: {
    fontSize: 13,
    marginBottom: 6,
  },
}));

const TotalRow = styled(SummaryRow)(({ theme }) => ({
  fontWeight: 700,
  fontSize: theme.breakpoints.down("md") ? 16 : 18,
  borderTop: "1px solid #333",
  paddingTop: theme.breakpoints.down("md") ? 8 : 12,
  marginTop: theme.breakpoints.down("md") ? 8 : 12,
  [theme.breakpoints.down("sm")]: {
    fontSize: 15,
    paddingTop: 6,
    marginTop: 6,
  },
}));

const PaymentFormContainer = styled(Box)(({ theme }) => ({
  background: "#181D23",
  borderRadius: theme.breakpoints.down("md") ? 16 : 20,
  padding: theme.breakpoints.down("md") ? "20px 16px" : "24px",
  marginBottom: theme.breakpoints.down("md") ? 16 : 24,
  [theme.breakpoints.down("sm")]: {
    padding: "16px 12px",
    marginBottom: 12,
  },
}));

const FormTitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.breakpoints.down("md") ? 18 : 20,
  fontWeight: 600,
  marginBottom: theme.breakpoints.down("md") ? 16 : 20,
  color: "#fff",
  [theme.breakpoints.down("sm")]: {
    fontSize: 16,
    marginBottom: 12,
  },
}));

const PaymentMethodContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.breakpoints.down("md") ? 8 : 12,
  marginBottom: theme.breakpoints.down("md") ? 16 : 20,
  flexWrap: "wrap",
  [theme.breakpoints.down("sm")]: {
    gap: 6,
    marginBottom: 12,
  },
}));

const PaymentDetailsPageContent = () => {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const { showNotification } = useNotification();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [clientSecret, setClientSecret] = useState<string>("");
  const [cardInfo, setCardInfo] = useState<CardInfo>({
    number: "",
    expiry: "",
    cvc: "",
    type: "VISA",
  });

  const [isUploading, setIsUploading] = useState(false);
  const [bookingDetails, setBookingDetails] = useState<BookingDetails | null>(
    null
  );

  
  useEffect(() => {
    // Get booking details from session storage
    const storedDetails = sessionStorage.getItem("bookingDetails");
    if (!storedDetails) {
      showNotification("No booking details found. Please start over.", "error");
      navigate("/");
      return;
    }
    const parsedDetails = JSON.parse(storedDetails);
    
    // If it's an extension payment, adjust the booking details
    if (parsedDetails.isExtension) {
      parsedDetails.insurancePrice = 0;
      parsedDetails.taxesAndFees = 0;
      parsedDetails.overstayPrice = 0;
      parsedDetails.total = parsedDetails.baseRate;
    }
    
    setBookingDetails(parsedDetails);

    // Create PaymentIntent
    const createPaymentIntent = async () => {
      try {
        const details = parsedDetails.isExtension ? parsedDetails : JSON.parse(storedDetails);
        const data = await paymentService.createPaymentIntent(
          Math.round(details.total * 100),
          details.bookingId
        );
        setClientSecret(data.clientSecret);
      } catch (error) {
        console.error("Failed to create payment intent:", error);
        showNotification(
          "Failed to initialize payment system. Please try again.",
          "error"
        );
      }
    };

    createPaymentIntent();
  }, [navigate, showNotification]);

  const handlePayment = async () => {
    if (!bookingDetails || !stripe || !elements) {
      showNotification("Payment system not ready. Please try again.", "error");
      return;
    }
  
    try {
      setIsUploading(true);
      showNotification("Processing your payment...", "info");
  
      // 1. Confirm Stripe payment
      const { paymentIntent, error: paymentError } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
        confirmParams: {
          return_url: `${window.location.origin}/booking-confirmation`,
        },
      });
  
      if (paymentError) {
        throw new Error(paymentError.message);
      }
  
      // Extract payment intent ID
      const paymentIntentId = paymentIntent.id;
      
      // Note: Card last4 cannot be reliably retrieved from the frontend payment intent
      // The backend should retrieve it using the payment intent ID with Stripe secret key
      // For now, we'll send the payment intent ID and let the backend handle card details retrieval
      let cardLast4: string | undefined;
      
      // Try to extract from payment intent (may not be available)
      try {
        const pi = paymentIntent as any;
        
        // Try payment_method object if expanded
        if (pi.payment_method && typeof pi.payment_method === 'object' && pi.payment_method.card) {
          cardLast4 = pi.payment_method.card.last4;
        }
        
        // Try latest_charge if expanded
        if (!cardLast4 && pi.latest_charge && typeof pi.latest_charge === 'object') {
          cardLast4 = pi.latest_charge.payment_method_details?.card?.last4;
        }
        
        // Try charges array if expanded
        if (!cardLast4 && pi.charges?.data?.[0]) {
          cardLast4 = pi.charges.data[0].payment_method_details?.card?.last4;
        }
      } catch (err) {
        // Silently fail - backend will retrieve it
      }
  
      // 2. Send payment record to backend
      // IMPORTANT: The backend should retrieve card_last4 from Stripe using the payment intent ID
      // because the frontend payment intent doesn't include expanded payment method details.
      // Backend can use: stripe.paymentIntents.retrieve(paymentIntentId, { expand: ['payment_method'] })
      // or retrieve the charge and get card details from there.
      await paymentService.createPaymentRecord({
        bookingId: bookingDetails.bookingId,
        amount: bookingDetails.total,
        paymentStatus: paymentIntent.status === "succeeded" ? "completed" : "pending",
        paymentMethod: "card",
        transactionReference: paymentIntentId,
        paymentDetails: {
          stripe_id: paymentIntentId,
          // card_last4 included if available from frontend, but backend should retrieve it as fallback
          ...(cardLast4 && { card_last4: cardLast4 }),
        },
      });
  
      // 3. Update booking status to "confirmed" after successful payment
      if (paymentIntent.status === "succeeded") {
        try {
          await bookingService.updatePaymentStatus({
            bookingId: bookingDetails.bookingId,
            paymentIntentId: paymentIntentId,
            status: "confirmed"
          });
        } catch (error) {
          console.error("Failed to update booking status:", error);
          // Continue even if status update fails - payment was successful
        }
      }
  
      showNotification("Payment successful!", "success");

      // Save booking id for confirmation page
      sessionStorage.setItem("bookingId", bookingDetails.bookingId);
      
      // If it's an extension payment, save extension data for confirmation page
      if (bookingDetails.isExtension) {
        const extensionData = sessionStorage.getItem('extensionData');
        if (extensionData) {
          sessionStorage.setItem('extensionData', extensionData);
        }
      }

      // Redirect to confirmation (session storage will be cleared there after processing)
      window.location.href = "/booking-confirmation";
  
    } catch (error) {
      console.error("Payment error:", error);
      showNotification("Payment failed. Please try again.", "error");
    } finally {
      setIsUploading(false);
    }
  };
  

  if (!bookingDetails) {
    return null; // or loading spinner
  }

  return (
    <PageContainer>
      <HeroSection>
        <Header />
        <MainContent>
          <Typography
            variant={isMobile ? "h3" : "h2"}
            sx={{
              textAlign: "center",
              color: "#fff",
              mb: isMobile ? 2 : 3,
            }}
          >
            Payment Details
          </Typography>

          <Grid container spacing={isMobile ? 2 : 3}>
            {/* Card Information */}
            <Grid size={{ xs: 12, lg: 12 }}>
              <StyledCard>
                <CardContent sx={{ p: isMobile ? 2 : 4 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      color: "#fff",
                    }}
                  >
                    Card Information
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <img src="icons/visa.svg" alt="visa" />
                    <img src="icons/master-card.svg" alt="mastercard" />
                    <img src="icons/amex.svg" alt="amex" />
                  </Stack>
                  <PaymentElement
                    options={{
                      layout: "tabs",
                      defaultValues: {
                        billingDetails: {
                          name: bookingDetails?.guestInfo?.name || '',
                          email: bookingDetails?.guestInfo?.email || '',
                          phone: bookingDetails?.guestInfo?.phone || '',
                        },
                      },
                    }}
                  />
                </CardContent>
              </StyledCard>
            </Grid>

            {/* Order Summary */}
            <Grid size={{ xs: 12, lg: 12 }}>
              <StyledCard>
                <CardContent sx={{ p: isMobile ? 2 : 4 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      color: "#fff",
                    }}
                  >
                    Order Summary
                  </Typography>

                  <Stack spacing={1} sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        color: "#b0b8c1",
                      }}
                    >
                      <Typography variant="body2">Base Rate</Typography>
                      <Typography variant="body2">
                        ${bookingDetails.baseRate.toFixed(2)}
                      </Typography>
                    </Box>
                    {!bookingDetails.isExtension && (
                      <>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            color: "#b0b8c1",
                          }}
                        >
                          <Typography variant="body2">
                            {bookingDetails.selectedInsurance === "premium"
                              ? "Premium Insurance"
                              : "Basic Insurance"}
                          </Typography>
                          <Typography variant="body2">
                            ${bookingDetails.insurancePrice.toFixed(2)}
                          </Typography>
                        </Box>
                        {bookingDetails.hasOverstayProtection && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              color: "#b0b8c1",
                            }}
                          >
                            <Typography variant="body2">
                              Overstay Protection
                            </Typography>
                            <Typography variant="body2">
                              ${bookingDetails.overstayPrice.toFixed(2)}
                            </Typography>
                          </Box>
                        )}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            color: "#b0b8c1",
                          }}
                        >
                          <Typography variant="body2">Taxes & Fees</Typography>
                          <Typography variant="body2">
                            ${bookingDetails.taxesAndFees.toFixed(2)}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Stack>

                  <Divider sx={{ my: 2, borderColor: "#42515E" }} />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 3,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, color: "#fff" }}
                    >
                      Total
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, color: "#fff" }}
                    >
                      ${bookingDetails.total.toFixed(2)}
                    </Typography>
                  </Box>

                  <PaymentButton
                    onClick={handlePayment}
                    disabled={isUploading}
                    startIcon={<img src="icons/lock.svg" alt="arrow-right" />}
                  >
                    {isUploading ? "Processing..." : "Complete Payment"}
                  </PaymentButton>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      mt: 2,
                      gap: 4,
                      flexWrap: "wrap",
                    }}
                  >
                    <Stack direction="row" spacing={1}>
                      <img src="icons/secure-icon.svg" alt="stripe" />
                      <Typography variant="body2">
                        Secure Payment Processing
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1}>
                      <img src="icons/stripe.svg" alt="stripe" />
                      <Typography variant="body2">Powered By Stripe</Typography>
                    </Stack>
                  </Box>
                </CardContent>
              </StyledCard>
            </Grid>
          </Grid>
        </MainContent>
      </HeroSection>
      <Footer />
    </PageContainer>
  );
};

const PaymentDetailsPage = () => {
  const [clientSecret, setClientSecret] = useState<string>("");

  useEffect(() => {
    const createPaymentIntent = async () => {
      try {
        const storedDetails = sessionStorage.getItem("bookingDetails");
        if (!storedDetails) return;

        const details = JSON.parse(storedDetails);
        const data = await paymentService.createPaymentIntent(
          Math.round(details.total * 100),
          details.bookingId
        );
        setClientSecret(data.clientSecret);
      } catch (error) {
        console.error("Failed to create payment intent:", error);
      }
    };

    createPaymentIntent();
  }, []);

  const appearance: Appearance = {
    theme: "night",
    variables: {
      colorPrimary: "#2563EB",
      colorBackground: "#181D23",
      colorText: "#fff",
      colorDanger: "#ef4444",
      fontFamily: "system-ui, -apple-system, sans-serif",
      spacingUnit: "4px",
      borderRadius: "8px",
    },
    rules: {
      ".Input": {
        backgroundColor: "#232933",
        border: "1px solid #42515E",
        color: "#fff",
      },
      ".Input:focus": {
        borderColor: "#2563EB",
      },
      ".Label": {
        color: "#b0b8c1",
      },
      ".Tab": {
        backgroundColor: "#232933",
        color: "#b0b8c1",
      },
      ".Tab--selected": {
        backgroundColor: "#2563EB",
        color: "#fff",
      },
    },
  };

  if (!stripePromise) {
    return (
      <PageContainer>
        <HeroSection>
          <Header />
          <MainContent>
            <Typography
              variant="h2"
              sx={{ textAlign: "center", color: "#fff" }}
            >
              Payment System Unavailable
            </Typography>
            <Typography sx={{ textAlign: "center", color: "#b0b8c1", mt: 2 }}>
              We're experiencing technical difficulties with our payment system.
              Please try again later.
            </Typography>
          </MainContent>
        </HeroSection>
        <Footer />
      </PageContainer>
    );
  }

  if (!clientSecret) {
    return (
      <PageContainer>
        <HeroSection>
          <Header />
          <MainContent>
            <Typography
              variant="h2"
              sx={{ textAlign: "center", color: "#fff" }}
            >
              Loading Payment System...
            </Typography>
          </MainContent>
        </HeroSection>
        <Footer />
      </PageContainer>
    );
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
      <PaymentDetailsPageContent />
    </Elements>
  );
};

export default PaymentDetailsPage;
